import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface CpdModule {
  id: number;
  title: string;
  description: string;
  roles: string[];
  topicArea: string;
  estimatedMinutes: number;
  cpdPoints: number;
  nmcStandards: string[];
  difficulty: string;
  badgeName: string;
  badgeColor: string;
  sections: unknown;
  isPublished: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

type MForm = Omit<CpdModule, "id" | "createdAt" | "updatedAt">;

const EMPTY: MForm = {
  title: "", description: "", roles: [], topicArea: "", estimatedMinutes: 30,
  cpdPoints: 1, nmcStandards: [], difficulty: "foundation",
  badgeName: "", badgeColor: "teal", sections: [], isPublished: true, sortOrder: 0,
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? "Request failed"); }
  return res.json();
}

function MForm({ value, onChange }: { value: MForm; onChange: (v: MForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const arrStr = (a: string[]) => a.join(", ");
  const strArr = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      <div>
        <Label>Title *</Label>
        <Input value={value.title} onChange={(e) => set("title", e.target.value)} />
      </div>
      <div>
        <Label>Description *</Label>
        <Textarea rows={2} value={value.description} onChange={(e) => set("description", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Topic area *</Label>
          <Input value={value.topicArea} onChange={(e) => set("topicArea", e.target.value)} placeholder="e.g. Medication Safety" />
        </div>
        <div>
          <Label>Difficulty *</Label>
          <Select value={value.difficulty} onValueChange={(v) => set("difficulty", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="foundation">Foundation</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Est. minutes</Label>
          <Input type="number" value={value.estimatedMinutes} onChange={(e) => set("estimatedMinutes", parseInt(e.target.value, 10) || 30)} />
        </div>
        <div>
          <Label>CPD points</Label>
          <Input type="number" value={value.cpdPoints} onChange={(e) => set("cpdPoints", parseInt(e.target.value, 10) || 1)} />
        </div>
        <div>
          <Label>Sort order</Label>
          <Input type="number" value={value.sortOrder} onChange={(e) => set("sortOrder", parseInt(e.target.value, 10) || 0)} />
        </div>
      </div>
      <div>
        <Label>Roles (comma-separated, or "all")</Label>
        <Input value={arrStr(value.roles)} onChange={(e) => set("roles", strArr(e.target.value))} placeholder="nurse, doctor, pharmacist" />
      </div>
      <div>
        <Label>NMC standards (comma-separated)</Label>
        <Input value={arrStr(value.nmcStandards)} onChange={(e) => set("nmcStandards", strArr(e.target.value))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Badge name *</Label>
          <Input value={value.badgeName} onChange={(e) => set("badgeName", e.target.value)} placeholder="e.g. Monitoring Pro" />
        </div>
        <div>
          <Label>Badge colour</Label>
          <Input value={value.badgeColor} onChange={(e) => set("badgeColor", e.target.value)} placeholder="teal" />
        </div>
      </div>
      <div>
        <Label>Sections (JSON array)</Label>
        <Textarea rows={4} value={JSON.stringify(value.sections, null, 2)} onChange={(e) => {
          try { set("sections", JSON.parse(e.target.value)); } catch { set("sections", e.target.value); }
        }} className="font-mono text-xs" />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={value.isPublished} onCheckedChange={(v) => set("isPublished", v)} />
        <Label>Published</Label>
      </div>
    </div>
  );
}

export default function AdminModules() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<CpdModule | null>(null);
  const [form, setForm] = useState<MForm>(EMPTY);

  const { data: modules = [], isLoading } = useQuery<CpdModule[]>({
    queryKey: ["admin", "modules"],
    queryFn: () => apiFetch("/api/admin/content/modules"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: MForm) =>
      editing
        ? apiFetch(`/api/admin/content/modules/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : apiFetch("/api/admin/content/modules", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "modules"] }); setDialogOpen(false); toast({ title: editing ? "Module updated" : "Module created" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/modules/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "modules"] }); toast({ title: "Module deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(m: CpdModule) {
    setEditing(m);
    setForm({ title: m.title, description: m.description, roles: m.roles, topicArea: m.topicArea, estimatedMinutes: m.estimatedMinutes, cpdPoints: m.cpdPoints, nmcStandards: m.nmcStandards, difficulty: m.difficulty, badgeName: m.badgeName, badgeColor: m.badgeColor, sections: m.sections, isPublished: m.isPublished, sortOrder: m.sortOrder });
    setDialogOpen(true);
  }

  const diffColor: Record<string, string> = { foundation: "bg-green-100 text-green-800", intermediate: "bg-yellow-100 text-yellow-800", advanced: "bg-red-100 text-red-800" };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Learning Modules"
        description={`${modules.length} module${modules.length !== 1 ? "s" : ""}`}
        action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New module</Button>}
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Title", "Topic", "Difficulty", "CPD", "Published", "Actions"]}
          rows={modules.map((m) => [
            <div key="title"><p className="font-medium text-slate-900 line-clamp-1">{m.title}</p><p className="text-xs text-slate-500">{m.roles.join(", ")}</p></div>,
            <span key="topic" className="text-sm text-slate-600">{m.topicArea}</span>,
            <span key="diff" className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${diffColor[m.difficulty] ?? "bg-slate-100 text-slate-700"}`}>{m.difficulty}</span>,
            <Badge key="cpd" variant="secondary" className="text-xs">{m.cpdPoints} pt{m.cpdPoints !== 1 ? "s" : ""}</Badge>,
            <span key="pub" className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.isPublished ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{m.isPublished ? "Yes" : "Draft"}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(m.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit module" : "New learning module"}</DialogTitle></DialogHeader>
          <MForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete module?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the learning module and all progress records.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
